// Helpers para extrair mensagens humanas de erros do Axios.
// Backend envia { error: { code, message, details? } }.

import axios from 'axios';

import type { ApiErrorResponse } from '@control-center/shared';

interface MessageMapping {
  [code: string]: string;
}

const DEFAULT_MESSAGES: MessageMapping = {
  VALIDATION_ERROR: 'Confira os campos do formulário.',
  UNAUTHORIZED: 'Sessão expirou. Entre de novo.',
  FORBIDDEN: 'Você não tem permissão pra essa ação.',
  NOT_FOUND: 'Esse cliente não existe — ou foi removido.',
  CONFLICT: 'Conflito ao salvar. Confira se o documento já existe.',
  STALE_VERSION: 'Alguém editou esse cliente enquanto você estava aqui. Atualize a página.',
  VERSION_CONFLICT:
    'Alguém editou esse lead enquanto você estava aqui. Atualize a página e tente de novo.',
  LEAD_ALREADY_CONVERTED:
    'Lead já convertido em assinatura — não pode mais ser editado ou descartado.',
  RATE_LIMITED: 'Muitas tentativas. Aguarde alguns segundos.',
  EXTERNAL_API_ERROR: 'Serviço externo falhou. Tenta de novo em alguns segundos.',
  INTERNAL_ERROR: 'Erro no servidor. Já avisamos o time.',
};

/** Extrai o `code` do erro do backend (`{ error: { code } }`), ou null. */
export function extractErrorCode(error: unknown): string | null {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.data?.error?.code ?? null;
  }
  return null;
}

/** Extrai `error.details` do backend (ex: `{ missingFields: [...] }`), ou null. */
export function extractErrorDetails(error: unknown): Record<string, unknown> | null {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    const details = error.response?.data?.error?.details;
    return (details as Record<string, unknown> | undefined) ?? null;
  }
  return null;
}

export function extractErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    const code = error.response?.data?.error?.code;
    const serverMessage = error.response?.data?.error?.message;
    if (code && DEFAULT_MESSAGES[code]) {
      return serverMessage && serverMessage.length < 160 ? serverMessage : DEFAULT_MESSAGES[code];
    }
    if (serverMessage && serverMessage.length < 160) return serverMessage;
    if (error.message === 'Network Error') {
      return 'Sem conexão com o servidor. Verifique sua rede.';
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
